import React, { useEffect, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';

/**
 * LanguagePrompt, first-visit banner for AR / DE / ES audiences.
 *
 * Triggers exactly once per browser when:
 *   1. navigator.language starts with ar | de | es
 *   2. glowmeets.lang is unset (user has not manually picked)
 *   3. glowmeets.langPromptDismissed is unset
 *   4. glowmeets.feature.langPrompt is unset OR === 'true'
 *   5. current path is not /contact or /faq (single-task surfaces)
 *
 * Slides in from the top edge over 320ms ease-out. 48px tall navy band.
 * Dismisses on Yes / Stay in English / any click outside the banner.
 */

const LANG_COPY = {
  ar: {
    line: 'قراءة هذه الصفحة بالعربية؟',
    yes: 'نعم',
    targetLang: 'AR',
    dir: 'rtl',
  },
  de: {
    line: 'Diese Seite auf Deutsch lesen?',
    yes: 'JA',
    targetLang: 'DE',
    dir: 'ltr',
  },
  es: {
    line: '¿Leer esta página en español?',
    yes: 'SÍ',
    targetLang: 'ES',
    dir: 'ltr',
  },
};

const STORAGE = {
  lang: 'glowmeets.lang',
  dismissed: 'glowmeets.langPromptDismissed',
  feature: 'glowmeets.feature.langPrompt',
};

const SUPPRESS_PATHS = ['/contact', '/faq'];

const LanguagePrompt = () => {
  const { setLang } = useLang();
  const [detected, setDetected] = useState(null); // 'ar' | 'de' | 'es' | null
  const [visible, setVisible] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Feature flag: default ON, can be flipped without deploy
    const featureFlag = window.localStorage.getItem(STORAGE.feature);
    if (featureFlag === 'false') return;

    // Already chose a language
    if (window.localStorage.getItem(STORAGE.lang)) return;

    // Already dismissed
    if (window.localStorage.getItem(STORAGE.dismissed)) return;

    // Path suppression
    if (SUPPRESS_PATHS.includes(window.location.pathname)) return;

    const navLang = (window.navigator.language || '').slice(0, 2).toLowerCase();
    if (!['ar', 'de', 'es'].includes(navLang)) return;

    setDetected(navLang);

    // Slide in after first paint settles
    const t = setTimeout(() => setVisible(true), 350);
    return () => clearTimeout(t);
  }, []);

  // Click-outside dismissal, fires on any document click outside the banner.
  // Does NOT set glowmeets.lang (banner can return on a future browser visit).
  useEffect(() => {
    if (!visible) return;
    const handler = (e) => {
      const banner = document.getElementById('glowmeets-lang-prompt');
      if (banner && !banner.contains(e.target)) {
        dismiss(false);
      }
    };
    // Slight delay so the slide-in itself doesn't immediately register a click
    const t = setTimeout(() => {
      document.addEventListener('click', handler, { capture: true });
    }, 600);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', handler, { capture: true });
    };
  }, [visible]);

  const persistDismissed = () => {
    try {
      window.localStorage.setItem(STORAGE.dismissed, 'true');
    } catch (e) {
      /* localStorage unavailable */
    }
  };

  const dismiss = (lockEnglish) => {
    if (dismissing) return;
    setDismissing(true);
    persistDismissed();
    if (lockEnglish) {
      try {
        window.localStorage.setItem(STORAGE.lang, 'EN');
      } catch (e) {
        /* ignore */
      }
    }
    // Slide out, then unmount
    setVisible(false);
    setTimeout(() => setDetected(null), 360);
  };

  const acceptLang = (e) => {
    e.stopPropagation();
    if (!detected) return;
    setLang(LANG_COPY[detected].targetLang);
    persistDismissed();
    setVisible(false);
    setTimeout(() => setDetected(null), 360);
  };

  if (!detected) return null;

  const copy = LANG_COPY[detected];

  return (
    <div
      id="glowmeets-lang-prompt"
      data-testid="lang-prompt"
      role="region"
      aria-label="Language preference"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '48px',
        backgroundColor: '#0A132F',
        borderBottom: '1px solid rgba(214,168,122,0.35)',
        zIndex: 60,
        transform: visible ? 'translateY(0)' : 'translateY(-100%)',
        transition: 'transform 320ms cubic-bezier(0.22, 1, 0.36, 1)',
        direction: copy.dir,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="h-full max-w-container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center gap-4 sm:gap-6">
        <span
          className="text-center"
          style={{
            fontFamily: copy.dir === 'rtl' ? 'Tajawal, Inter, sans-serif' : 'Inter, sans-serif',
            fontSize: window.innerWidth < 640 ? '9pt' : '11pt',
            fontWeight: 500,
            letterSpacing: copy.dir === 'rtl' ? '0.06em' : '0.18em',
            textTransform: copy.dir === 'rtl' ? 'none' : 'uppercase',
            color: '#D6A87A',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {copy.line}
        </span>

        <button
          type="button"
          onClick={acceptLang}
          data-testid="lang-prompt-yes"
          aria-label={`Switch to ${copy.targetLang}`}
          style={{
            fontFamily: copy.dir === 'rtl' ? 'Tajawal, Inter, sans-serif' : 'Inter, sans-serif',
            fontSize: '10pt',
            fontWeight: 600,
            letterSpacing: copy.dir === 'rtl' ? '0.04em' : '0.18em',
            textTransform: copy.dir === 'rtl' ? 'none' : 'uppercase',
            color: '#0A132F',
            backgroundColor: '#D6A87A',
            border: '1px solid #D6A87A',
            padding: '0 16px',
            borderRadius: '999px',
            height: window.innerWidth < 640 ? '28px' : '32px',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease, border-color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#B98556';
            e.currentTarget.style.borderColor = '#B98556';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#D6A87A';
            e.currentTarget.style.borderColor = '#D6A87A';
          }}
        >
          {copy.yes}
        </button>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            dismiss(true);
          }}
          data-testid="lang-prompt-stay"
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '10pt',
            fontWeight: 600,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: '#D6A87A',
            backgroundColor: 'transparent',
            border: '1px solid #D6A87A',
            padding: '0 16px',
            borderRadius: '999px',
            height: window.innerWidth < 640 ? '28px' : '32px',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease, color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#D6A87A';
            e.currentTarget.style.color = '#0A132F';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#D6A87A';
          }}
        >
          Stay in English
        </button>
      </div>
    </div>
  );
};

export default LanguagePrompt;
